import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { ToastStack } from './ToastStack';
import type { ToastItem, ToastOpcoes, ToastVariant } from './types';

export type ToastContextValue = {
  show: (variant: ToastVariant, message: string, opcoes?: ToastOpcoes) => void;
  success: (message: string, opcoes?: ToastOpcoes) => void;
  error: (message: string, opcoes?: ToastOpcoes) => void;
  warning: (message: string, opcoes?: ToastOpcoes) => void;
  info: (message: string, opcoes?: ToastOpcoes) => void;
};

export const ToastContext = createContext<ToastContextValue | null>(null);

let toastId = 0;

const DURACAO: Record<ToastVariant, number> = {
  success: 4000,
  info: 4500,
  warning: 5200,
  error: 6500,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((variant: ToastVariant, message: string, opcoes?: ToastOpcoes) => {
    const id = ++toastId;
    const duracao = opcoes?.durationMs === undefined ? DURACAO[variant] : opcoes.durationMs;
    setItems((prev) => [
      ...prev,
      { id, variant, message, durationMs: duracao, onClick: opcoes?.onClick },
    ]);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (m, opcoes) => show('success', m, opcoes),
      error: (m, opcoes) => show('error', m, opcoes),
      warning: (m, opcoes) => show('warning', m, opcoes),
      info: (m, opcoes) => show('info', m, opcoes),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStack items={items} onDismiss={remove} />
    </ToastContext.Provider>
  );
}
